import type { Quote } from '@/services/quotes';
import type { PublicMaterial } from '@/services/materials';
import { MaterialCard } from './MaterialCard';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface QuoteResultProps {
  readonly quote: Quote | undefined;
  readonly isLoading: boolean;
  readonly selectedMaterialId?: string | null;
  readonly onSelectMaterial?: (materialId: string) => void;
  readonly materials?: readonly PublicMaterial[];
}


export const QuoteResult = ({ quote, isLoading, selectedMaterialId, onSelectMaterial, materials }: QuoteResultProps) => {
  if (isLoading || !quote) {
    return (
      <div
        className="flex flex-col items-center"
        style={{
          gap: 'var(--space-4)',
          paddingTop: 'var(--space-12)',
          paddingBottom: 'var(--space-12)',
        }}
      >
        <LoadingSpinner size="lg" />
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
          Analyzing your model...
        </p>
      </div>
    );
  }

  if (quote.status === 'processing') {
    return (
      <div
        className="flex flex-col items-center"
        style={{
          gap: 'var(--space-4)',
          paddingTop: 'var(--space-12)',
          paddingBottom: 'var(--space-12)',
        }}
      >
        <LoadingSpinner size="lg" />
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
          Processing your file. This usually takes a few seconds...
        </p>
      </div>
    );
  }

  if (quote.status === 'review') {
    return (
      <div
        className="text-center"
        style={{
          borderRadius: 'var(--radius-lg)',
          backgroundColor: 'var(--color-primary-50)',
          padding: 'var(--space-6)',
          color: 'var(--color-primary-700)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          alignItems: 'center',
        }}
      >
        <p style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', margin: 0 }}>
          File received — manual review required
        </p>
        <p style={{ fontSize: 'var(--text-sm)', margin: 0, maxWidth: '480px' }}>
          Your file has been uploaded successfully. Since it is not a 3MF file, automatic analysis is not available.
          Our team will review your model and provide a quote. For instant pricing, upload a .3mf file instead.
        </p>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-500)', margin: 0 }}>
          3MF files include assembly data, textures, and print settings for the best results.
        </p>
      </div>
    );
  }

  if (quote.status === 'expired' || quote.status === 'rejected') {
    const message = quote.rejectionReason ?? 'Quote processing failed. Please try uploading again.';
    return (
      <div
        className="text-center"
        style={{
          borderRadius: 'var(--radius-lg)',
          backgroundColor: 'var(--color-error-50)',
          padding: 'var(--space-4)',
          color: 'var(--color-error-700)',
        }}
      >
        {message}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Model stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 'var(--space-4)' }}>
        <StatBox label="Volume" value={`${quote.volumeCm3} cm3`} />
        <StatBox label="Surface Area" value={`${quote.surfaceAreaCm2} cm2`} />
        {quote.boundingBox && (
          <>
            <StatBox
              label="Dimensions"
              value={`${quote.boundingBox.width} x ${quote.boundingBox.height} x ${quote.boundingBox.depth} cm`}
            />
            <StatBox label="File" value={quote.fileName} />
          </>
        )}
      </div>

      {/* Material prices */}
      <div>
        <h3
          style={{
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
          }}
        >
          Select a Material
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3" style={{ gap: 'var(--space-4)' }}>
          {quote.prices.map((price) => {
            const mat = materials?.find((m) => m.materialId === price.materialId);
            // Material is available if we have no stock data (backwards compat) or if it exists in the materials list
            const isAvailable = !materials || !!mat;
            // Check if any color is in stock for this material
            const hasStockColors = !mat || Object.values(mat.colorStock).some((v) => v);
            const selectable = isAvailable && hasStockColors;

            return (
              <div key={price.materialId} style={{ position: 'relative' }}>
                <MaterialCard
                  materialId={price.materialId}
                  name={mat?.name ?? price.materialId}
                  description={mat?.description ?? ''}
                  price={price.price}
                  estimatedDays={price.estimatedDays}
                  isSelected={selectedMaterialId === price.materialId}
                  onSelect={selectable ? onSelectMaterial : undefined}
                  disabled={!selectable}
                />
                {!selectable && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 'var(--space-2)',
                      right: 'var(--space-2)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: '#991b1b',
                      backgroundColor: '#fef2f2',
                      padding: 'var(--space-1) var(--space-2)',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    Out of Stock
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value }: { label: string; value: string }) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-3)',
    }}
  >
    <p
      style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-wide)',
        color: 'var(--text-tertiary)',
      }}
    >
      {label}
    </p>
    <p
      className="truncate"
      style={{
        marginTop: 'var(--space-1)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-primary)',
      }}
    >
      {value}
    </p>
  </div>
);
